// Normaliza telefone brasileiro pra E.164 (+55DDXXXXXXXXX).
export function normalizePhoneBR(phone: string | null | undefined): string {
  if (!phone) return '';
  let digits = String(phone).replace(/\D/g, '');
  if (digits.startsWith('0')) digits = digits.slice(1);
  if (!digits.startsWith('55')) digits = '55' + digits;
  return '+' + digits;
}

/**
 * Separa o DDI do numero nacional — a UNICA definicao de "este 55 e' pais ou e'
 * DDD?" no sistema.
 *
 * ⚠️ **O comprimento e' o que decide, nunca o prefixo sozinho.** `55` tambem e' o
 * DDD de Santa Maria/RS, entao `5599943884` (10 digitos) e' um numero gaucho e nao
 * um numero com DDI. So ha DDI quando sobram 10 ou 11 digitos embaixo dele — ou
 * seja, quando o total e' 12 ou 13.
 *
 * ⛔ **Existia escrito TRES vezes, de tres jeitos** (20/08/2026), e uma delas estava
 * errada: o `formatTelefoneBR` fazia `.replace(/^55/, '')` incondicional e comia o
 * DDD do gaucho. Nao ha nenhum DDD 55 na base hoje — a correcao fecha uma porta, nao
 * conserta registro —, mas tres copias da mesma regra ja e' o defeito.
 *
 * Medido na base em 20/08/2026: **todo** numero com mais de 11 digitos comeca com
 * 55 (35 de 12 digitos, 83 de 13), sem uma excecao.
 */
export function separarDdiBR(raw: string | null | undefined): { ddi: string; nacional: string } {
  const d = String(raw ?? '').replace(/\D/g, '');
  const temDdi = d.startsWith('55') && (d.length === 12 || d.length === 13);
  return temDdi ? { ddi: '55', nacional: d.slice(2) } : { ddi: '', nacional: d };
}

/**
 * Os digitos NACIONAIS de um telefone brasileiro, tirando o que vem grudado na
 * frente quando o numero chega COLADO em vez de digitado.
 *
 * ⚠️ **Duas coisas entram por fora, e as duas quebram a mascara em silencio**:
 * o **DDI** (`+55`, que e' como o WhatsApp copia) e o **prefixo de selecao de
 * operadora** (`0` + XX — `021`, `015`, `041`), que e' como o contato costuma
 * estar salvo na agenda e como o **autofill do iPhone** o oferece em cima do
 * teclado. Cortar em 11 pelo FIM, nesses casos, guarda os digitos errados:
 * `02164992326324` virava `(02) 16499-2326` e `+55 64 99232-6324` virava
 * `(55) 64992-3263` — medido em prod no cadastro de cliente do AppCel em
 * 30/08/2026, com o numero que o proprio iPhone dele estava sugerindo.
 *
 * ⚠️ **O DDI quem decide e' o `separarDdiBR`**, nunca um `replace(/^55/)` — `55`
 * tambem e' DDD (Santa Maria/RS), e essa e' a unica definicao da casa.
 */
export function nacionalTelefoneBR(raw: string | null | undefined): string {
  const d = String(raw ?? '').replace(/\D/g, '');
  if (!d) return '';
  // ⚠️ O zero da frente aparece em TRES formas, e cortar sempre 3 digitos so' acerta uma delas
  // (medido em 10/09/2026, [DA-708]): `021` + numero (selecao de operadora, como o autofill do
  // iPhone oferece), `0` + DDD + numero (o zero antigo de discar DDD, que e' como o `<fone>` de
  // uma NF-e chegou: `(06) 23605-1400`), e `55` + `0` + numero, com o DDI ANTES do zero
  // (`55064992993511`, um WhatsApp de fornecedor em prod).
  // Entao as formas sao TENTADAS e quem decide e' qual delas fecha num numero brasileiro.
  const candidatos: string[] = [];
  const poe = (x: string) => {
    const n = separarDdiBR(x).nacional;
    if (n && !candidatos.includes(n)) candidatos.push(n);
  };
  poe(d);
  if (d.startsWith('0')) { poe(d.slice(1)); poe(d.slice(3)); }
  if (d.startsWith('55') && d.length > 13) {
    const resto = d.slice(2);
    poe(resto);
    if (resto.startsWith('0')) { poe(resto.slice(1)); poe(resto.slice(3)); }
  }
  // ⛔ Nacional que comeca com `0` NAO fecha: nao existe DDD com zero na frente. Sem isso,
  // `06236051400` (11 digitos) passaria por celular valido e o zero viraria parte do DDD.
  const bom = candidatos.find((c) => (c.length === 10 || c.length === 11) && !c.startsWith('0'));
  return bom ?? candidatos[0] ?? d;
}

/**
 * O texto que o campo de telefone mostra enquanto se digita — `(64) 99232-6324`.
 *
 * ⚠️ **Formata o que ja tem, sem completar nada**: 3 digitos saem `(64) 9`, e e'
 * isso que faz a mascara servir a digitacao. O corte em 11 e' o teto do numero
 * nacional; o que vem por fora (DDI, operadora) sai ANTES, no `nacionalTelefoneBR`.
 */
export function mascaraTelefoneBR(raw: string | null | undefined): string {
  const d = nacionalTelefoneBR(raw).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/**
 * O primeiro telefone brasileiro que aparece DENTRO de um texto qualquer.
 *
 * ⚠️ **Existe pro botao "Colar"**: o que vem da area de transferencia raramente e'
 * o numero limpo — e' *"Meu contato: +55 64 99232-6324"*, ou um pedaco de conversa
 * com outros numeros antes. Jogar o texto inteiro na mascara gruda tudo (ela so
 * tira os nao-digitos), entao o telefone precisa ser ACHADO antes de ser formatado.
 *
 * Devolve `null` quando nao ha nada com cara de telefone — e' o que faz o botao
 * poder dizer *"nao achei um telefone"* em vez de escrever lixo no campo.
 */
export function telefoneNoTexto(texto: string | null | undefined): string | null {
  const t = String(texto ?? '');

  // 1) Bloco CRU de digitos (`02164992326324`, `5564992326324`). Vem primeiro e
  //    exige estar isolado: o padrao com separadores, solto num bloco de 14
  //    digitos, casa os 11 primeiros e joga o final fora — o mesmo estrago que
  //    esta funcao existe pra evitar. (Foi o teste que pegou isto, em 30/08/2026.)
  const cru = /(?:^|\D)(\d{10,14})(?!\d)/.exec(t);
  if (cru?.[1]) return cru[1];

  // 2) Com separadores: DDI opcional, DDD com ou sem parenteses, 4 ou 5 + 4.
  //    ⛔ Sem `lookbehind` de proposito — ele so existe no Safari 16.4+, e um
  //    regex que o navegador nao sabe ler nao "degrada": ele estoura na hora de
  //    ler o arquivo e derruba a tela inteira.
  const m = /(?:\+?\s?55[\s.-]?)?\(?\d{2}\)?[\s.-]?\d{4,5}[\s.-]?\d{4}(?!\d)/.exec(t);
  return m ? m[0] : null;
}

// Gera todas as variantes plausiveis de um telefone BR pra casar numeros
// equivalentes: com E sem DDI 55, com E sem o 9o digito do celular. Recebe
// o numero cru (com mascara, prefixo, etc) — usa so os digitos.
export function variantesTelefoneBR(raw: string | null | undefined): string[] {
  const d = String(raw ?? '').replace(/\D/g, '');
  if (!d) return [];
  const { nacional: semDdi } = separarDdiBR(d);
  const vs = new Set<string>([d]);
  const add = (n: string) => { vs.add(n); vs.add('55' + n); };
  add(semDdi);
  if (semDdi.length === 11 && semDdi[2] === '9') {
    add(semDdi.slice(0, 2) + semDdi.slice(3));        // mesma linha sem o 9o digito
  } else if (semDdi.length === 10) {
    add(semDdi.slice(0, 2) + '9' + semDdi.slice(2));  // mesma linha com o 9o digito
  }
  return [...vs];
}

/**
 * A forma CANONICA de GRAVAR um telefone: so' digitos, com DDI, sem mascara e sem `+`.
 *
 * ⚠️ **Mascara e `+` sao roupa e moram na tela** (`mascaraTelefoneBR`/`formatTelefoneBR`), nunca
 * no dado. Medido em 10/09/2026: metade dos 2.110 contatos estava gravada com separador
 * (`+5564 8157-0002`), e por isso a busca por trecho do numero achava no chat e nao achava no
 * "Enviar ao Caixa" — texto cru nao casa com texto cru quando um dos lados tem hifen.
 *
 * ⛔ **O 9o digito NUNCA e' inventado nem removido.** `556492326324` (como o WhatsApp guarda a
 * linha antiga) e `5564992326324` sao a MESMA pessoa e as duas formas sao canonicas — quem casa
 * as duas e' a `chave_telefone_br` no banco / o `variantesTelefoneBR` aqui, nao esta funcao.
 * Acrescentar o 9 aqui criaria divergencia com o `jid`, que e' quem entrega a mensagem.
 *
 * ⚠️ O DDI e o prefixo de operadora sao tirados pelo `nacionalTelefoneBR` — que ja sabe que `55`
 * tambem e' o DDD de Santa Maria e so' o descarta quando sobra numero embaixo.
 *
 * ⚠️ **Numero de FORA do Brasil mantem o `+`**: sao 17 na base, e ali o `+` e' a unica marca de
 * que ha DDI — sem ele, `1732824873` fica indistinguivel de um fixo de Catanduva. A funcao so'
 * consegue identifica-lo quando sobram 12+ digitos que nao fecham como BR; estrangeiro com 10 ou
 * 11 digitos e' ambiguo por construcao e sai como BR (esses chegam pelo `jid`, nao pelo campo).
 *
 * Devolve `''` quando nao da' pra reconhecer nada — quem chama decide entre gravar `null` e
 * recusar a entrada.
 */
export function canonicoTelefoneBR(raw: string | null | undefined): string {
  const cru = String(raw ?? '').trim();
  const d = cru.replace(/\D/g, '');
  if (!d) return '';
  // O `+` explicito quer dizer que o DDI JA esta no numero (e' o que E.164 significa). Se ele nao
  // e' 55, o numero e' de fora e sai preservado: por o `55` na frente de `+16787774551` fabricaria
  // um numero que nao existe. ⚠️ O preco e' que `+6492635006` (BR gravado sem DDI, com `+`) tambem
  // fica de fora — e' preferivel: preservar o dado torto e' menos grave que inventar outro numero,
  // e esses caem na conferencia manual, onde o `jid` desempata.
  if (cru.startsWith('+') && !d.startsWith('55') && d.length >= 8 && d.length <= 15) return '+' + d;
  const nacional = nacionalTelefoneBR(raw);
  if (nacional.length === 10 || nacional.length === 11) return '55' + nacional;
  if (d.length >= 8 && d.length <= 15) return '+' + d;
  return '';
}

export function formatTelefoneBR(val: string | null | undefined): string {
  if (!val) return '—';
  const { nacional: digits } = separarDdiBR(val);
  if (digits.length === 11) {
    return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  }
  if (digits.length === 10) {
    return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  return digits;
}
